import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Title, Text, Paper, Anchor } from '@mantine/core';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';

// Renderuje pojedynczy fragment ("run") tekstu prawnego: zwykły tekst, pogrubienie,
// kod inline, link wewnętrzny (to) lub zewnętrzny/mailto/tel (href).
function renderRun(run, key) {
  if (typeof run === 'string') return <React.Fragment key={key}>{run}</React.Fragment>;
  if (run.code) return <code key={key}>{run.t}</code>;
  if (run.to) return <Anchor key={key} component={Link} to={run.to} c="brand">{run.t}</Anchor>;
  if (run.href) {
    const extra = run.ext ? { target: '_blank', rel: 'noopener noreferrer' } : {};
    return <Anchor key={key} href={run.href} c="brand" {...extra}>{run.t}</Anchor>;
  }
  if (run.b) return <strong key={key}>{run.t}</strong>;
  return <React.Fragment key={key}>{run.t}</React.Fragment>;
}

// Runs = string (zwykły tekst) lub tablica fragmentów.
function Rich({ runs }) {
  if (typeof runs === 'string') return <>{runs}</>;
  return <>{(runs || []).map((r, i) => renderRun(r, i))}</>;
}

// Renderuje listę punktów sekcji. Element listy może być:
//  - stringiem (zwykły punkt),
//  - tablicą fragmentów (punkt z pogrubieniem/linkami),
//  - obiektem { lead, sub } (punkt z zagnieżdżoną listą).
function LegalList({ items }) {
  return (
    <ol className="list-decimal pl-5 space-y-3">
      {(items || []).map((item, i) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          return (
            <li key={i}>
              <Rich runs={item.lead} />
              {item.sub && (
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  {item.sub.map((s, j) => (
                    <li key={j}><Rich runs={s} /></li>
                  ))}
                </ul>
              )}
            </li>
          );
        }
        return <li key={i}><Rich runs={item} /></li>;
      })}
    </ol>
  );
}

const SECTIONS = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8', 's9', 's10', 's11', 's12', 's13'];

export default function PrivacyPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#F6F5F4]">
      <SEO title={t('privacy.seo.title')} description={t('privacy.seo.description')} path="/polityka-prywatnosci" />
      <Navbar />

      <section className="bg-[#F6F5F4] py-24">
        <Container size="md" px="md">
          <Title order={1} ta="center" mb={4} className="text-3xl md:text-5xl">
            {t('privacy.heading')}
          </Title>
          <Text c="dimmed" ta="center" mb={64}>
            {t('privacy.updated')}
          </Text>

          <Paper radius="lg" p={{ base: 32, md: 48 }} withBorder className="space-y-10 text-gray-700 leading-relaxed">

            {SECTIONS.map((key) => (
              <div key={key}>
                <Title order={3} mb="md">{t(`privacy.${key}.title`)}</Title>
                <LegalList items={t(`privacy.${key}.items`, { returnObjects: true })} />
              </div>
            ))}

            {/* Footer reference */}
            <div className="border-t border-gray-200 pt-8">
              <Text c="dimmed">
                <Rich runs={t('privacy.footer.ref', { returnObjects: true })} />
              </Text>
              <Text c="dimmed" size="sm" mt="xs">
                <Rich runs={t('privacy.footer.company', { returnObjects: true })} />
              </Text>
            </div>

          </Paper>
        </Container>
      </section>

      <Footer />
    </div>
  );
}
